import { useSelector } from 'react-redux';
import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { AppState } from 'app/App.store';
import AxiosClient from '@ui-core/config/axiosClient.js';
import { Dispatch } from 'redux';
import axios from 'axios';

export interface IContactExecutive {
	id: string;
	teamRole: string;
	rutUsuario: string;
	primerNombre: string;
	segundoNombre: string;
	apellidoPaterno: string;
	apellidoMaterno: string;
	correo: string;
	phone: string;
	mobilePhone: string;
	isDeleted: boolean;
	prioridad: number;
	rol: string;
}

export interface IYourExecutive{
	isOpen: boolean,
	isLoading: boolean,
	data: IContactExecutive[],
	error: boolean,
	errorMessage: string
}
export interface IUserData{
	email: string;
}

export interface IUserMenu{
	isLoading: boolean,
	data: IUserData,
	error: boolean,
	errorMessage: string,
	wasFetched:boolean
}
export interface HeaderState {
	initialLoad: boolean;
	yourExecutive: IYourExecutive,
	userMenu: IUserMenu
}

const initialState: HeaderState = {
	initialLoad: false,
	yourExecutive:{
		data: [],
		error: false,
		errorMessage: '',
		isLoading: false,
		isOpen: false
	},
	userMenu: {
		isLoading:false,
		data:{} as IUserData,
		error: false,
		errorMessage:'',
		wasFetched:false
	}
};

export const headerSlice = createSlice({
	initialState,
	name: 'header',
	reducers: {
		loadInitial: (state) => {
			state.initialLoad = true;
		},
		contactEnterpriseHandleOpen: (state, payload: PayloadAction<boolean>) => {
			state.yourExecutive.isOpen = payload.payload;
		},
		contactEnterpriseLoading: (state) => {
			state.yourExecutive.isLoading = true;
			state.yourExecutive.data = [];
			state.yourExecutive.error = false;
			state.yourExecutive.errorMessage = '';
		},
		contactEnterpriseSuccess: (state, payload: PayloadAction<IContactExecutive[]>) => {
			state.yourExecutive.isLoading = false;
			state.yourExecutive.data = payload.payload;
			state.yourExecutive.isOpen = true;

		},
		contactEnterpriseError: (state, payload: PayloadAction<string>) => {
			state.yourExecutive.isLoading = false;
			state.yourExecutive.error = false;
			state.yourExecutive.errorMessage = payload.payload;
		},
		userMenuLoading:(state) =>{
			state.userMenu.isLoading = true;
			state.userMenu.error = false;
			state.userMenu.errorMessage = '';
		},
		userMenuSuccess: (state, action: PayloadAction<IUserData>)=>{
			state.userMenu.isLoading = false;
			state.userMenu.data = action.payload;
			state.userMenu.error = false;
			state.userMenu.errorMessage = '';
			state.userMenu.wasFetched = true;
		},
		userMenuError: (state, action: PayloadAction<string>)=>{
			state.userMenu.isLoading = false;
			state.userMenu.error = true;
			state.userMenu.errorMessage = action.payload;
			state.userMenu.wasFetched = true;
		},
		userMenuEmailUpdate: (state, action: PayloadAction<string>)=>{
			state.userMenu.data = {...state.userMenu.data, email: action.payload};
		}
	},
});

export const {
	loadInitial,
	contactEnterpriseError,
	contactEnterpriseHandleOpen,
	contactEnterpriseLoading,
	contactEnterpriseSuccess,
	userMenuLoading,
	userMenuSuccess,
	userMenuError,
	userMenuEmailUpdate
} = headerSlice.actions;

export const useHeaderSelector = () =>
	useSelector<AppState, HeaderState>(
		({ [headerSlice.name]: slice }) => slice,
	);

export const initialLoading =
	() =>
	// eslint-disable-next-line unicorn/consistent-function-scoping, require-await
	async (dispatch: Dispatch): Promise<void> => {
		dispatch(loadInitial());
	};

/**
 * Funcion para buscar los equipos de cuenta de una empresa y mostrarlos
 * en la ventana flotante del boton tu ejecutivo
 * @returns { Promise<void> }
 */
export const getEquiposCuentaNavbar =
() =>
async (dispatch: Dispatch): Promise<void> => {
	dispatch(contactEnterpriseLoading())

	const axiosApi = AxiosClient(
		`${import.meta.env.REACT_APP_ADMIN_API_URL}`,
	);

	try {
		const rutEnterprise = localStorage.getItem('rutEnterprise') as string;

		const response = await axiosApi.get<IContactExecutive[]>(
			`v1/empresa/equipos?RUT=${rutEnterprise}`,
		);

		dispatch(contactEnterpriseSuccess(response.data));

	} catch (error: any) {
		if (axios.isAxiosError(error)) {
			dispatch(contactEnterpriseError(error.message));
		}
	}
};

export const fetchUserData =
	() =>
	async (dispatch: Dispatch): Promise<void> => {
		dispatch(userMenuLoading());
		const axiosApi = AxiosClient(
			`${import.meta.env.REACT_APP_HOME_API_URL}`,
		);

		try {
			const response = await axiosApi.get('v1/usuario/datos-generales');
			dispatch(userMenuSuccess(response.data));
		} catch (error: unknown) {
			if (axios.isAxiosError(error)) {
				console.error('Fetch user data error:', error.response);
				dispatch(userMenuError(error.response?.data.message));
			}
		}
	};
